<template>
	<div>
		<span class="report_card-header mb-4">{{ title }}</span>
		<div class="bar-chart" :id="`bar-chart-${this.subject}`"></div>
		<div class="ml-5 my-3 d-flex" :id="`bar-${this.subject}-legend`"></div>
	</div>
</template>

<script>
import { select } from "d3-selection";
import { scaleBand, scaleLinear } from "d3-scale";
import { axisBottom } from "d3-axis";
import { getCompetency } from "../../../utils/apiFunctions";

export default {
	name: "BarGraph",
	props: {
		subject: {
			type: String,
			validator: (value) => ["difficulty", "section"].indexOf(value) !== -1,
		},
		title: {
			type: String,
		},
	},
	data() {
		return {
			gradients: [
				{ id: "background", colors: ["#FFFFFF", "#FFFFFF"] },
				{ id: "incorrect", colors: ["#FDF9F0", "#FFCF26"] },
				{ id: "correct", colors: ["#E4FFF4", "#64E1A5"] },
				{ id: "unattempted", colors: ["#E9F2FF", "#49D3FF"] },
			],
			testId: this.$route.params.testID,
			candidateId: this.$route.params.candidateID,
			barData: [
				{ name: "1", incorrect: 2, correct: 3, unattempted: 5 },
				{ name: "2", incorrect: 3, correct: 1, unattempted: 2 },
				{ name: "3", incorrect: 0, correct: 6, unattempted: 3 },
				// {name: "4", incorrect: 1, correct: 0, unattempted: 8},
				// {name: "5", incorrect: 2, correct: 7, unattempted: 0},
				// {name: "6", incorrect: 2, correct: 7, unattempted: 0},
				// {name: "7", incorrect: 2, correct: 7, unattempted: 0},
				// {name: "8", incorrect: 2, correct: 7, unattempted: 0},
				// {name: "9", incorrect: 2, correct: 7, unattempted: 0},
				// {name: "10", incorrect: 2, correct: 7, unattempted: 0},
			],
			color: ["correct", "incorrect", "unattempted"],
			strokeColors: ["#3EC584", "#FFD12C", "#3379B3"],
		};
	},
	async mounted() {
		try {
			const response = await this.getData();
			this.barData = response.data;
			this.createChart();
		} catch (e) {
			console.error("Error getting data", e);
		}
	},
	methods: {
		async getData() {
			const { data } = await getCompetency(
				this.subject,
				this.testId,
				this.candidateId
			);
			if (data.type !== "Success") {
				throw new Error("Request Failed");
			} else {
				return data;
			}
		},

		createChart() {
			const margin = { top: 10, right: 30, bottom: 40, left: 20 };
			// Making width a relation of the number of bars to display
			const rawWidth = this.barData.length * 170;
			const width = rawWidth - margin.left - margin.right;
			const height = 450 - margin.top - margin.bottom;
			const interBarPadding = 3;
			const propertyNames = this.barData.map((data) => data.name);

			const normalisedData = this.barData.map((data) => {
				const total = data.correct + data.incorrect + data.unattempted;
				return {
					...data,
					total: total,
					percentages: [
						(data.correct / total) * 100,
						(data.incorrect / total) * 100,
						(data.unattempted / total) * 100,
					],
				};
			});
			const svg = select(`#bar-chart-${this.subject}`)
				.append("svg")
				.attr("preserveAspectRatio", "xMidYMid meet")
				.attr(
					"viewBox",
					`${margin.left} 0 ${width} ${height + margin.top + margin.bottom}`
				)
				.attr("width", width)
				.attr("height", height);
			//Adding gradients
			svg
				.selectAll("linearGradient")
				.data(this.gradients)
				.enter()
				.append("linearGradient")
				.attr("id", (d) => d.id)
				.attr("x1", "9")
				.attr("y1", "0")
				.attr("x2", "9")
				.attr("y2", "3")
				// Appending the data
				.selectAll("stop")
				.data((d) => d.colors)
				.enter()
				.append("stop")
				.attr("stop-color", (d) => d)
				.attr("stop-opacity", 0.8)
				.attr("offset", (_, i) => (i === 1 ? 1.5 : 0));

			const group = svg.append("g");

			const rects = group
				.append("g")
				.attr("transform", `translate(${margin.left}, 0)`);

			const xScale = scaleBand()
				.domain([...propertyNames])
				.range([margin.left, width])
				.paddingInner(0.5)
				.paddingOuter(0.4);

			const yScale = scaleLinear()
				.domain([0, 100])
				.range([height, 0]);

			const xAxis = axisBottom().scale(xScale);
			const designGroup = group.append("g");
			//Design
			// We add 30 px to add to the width for the padding
			const bgRectWidth = xScale.bandwidth() + 30;
			designGroup
				.append("g")
				.selectAll("rect")
				.data(normalisedData)
				.enter()
				.append("rect")
				.attr("rx", 20)
				.attr("fill", "url('#background')")
				.attr("stroke", "lightgrey")
				.attr("width", bgRectWidth)
				.attr("fill-opacity", 0)
				.attr("height", yScale(-10))
				.attr("x", (d) => xScale(d.name) + 3);

			designGroup
				.append("g")
				.selectAll("circle")
				.data(normalisedData)
				.enter()
				.append("circle")
				.attr("r", 8)
				.attr("cx", (d) => xScale(d.name))
				.attr("cy", yScale(-10))
				.attr("fill", "#B09FFF")
				.attr("fill-opacity", 0.8)
				.attr("transform", `translate(${bgRectWidth / 2 + 3}, 0)`)
				.attr("stroke", "#FFFFFF")
				.attr("stroke-width", 4);

			rects
				.selectAll("g")
				.data(normalisedData)
				.enter()
				.append("g")
				.attr(
					"transform",
					(data) => `translate(${xScale(data.name)}, ${height})`
				)
				.selectAll("rect")
				.data((d) => d.percentages)
				.enter()
				.append("rect")
				.attr("height", (d) => yScale(100 - d))
				.attr("width", xScale.bandwidth() / 3 - interBarPadding)
				.attr("rx", "4")
				.attr("fill", (_, i) => `url(#${this.color[i]})`)
				.attr("stroke", (_, i) => this.strokeColors[i])
				.attr("stroke-width", 0.4)
				.attr(
					"transform",
					(d, i) =>
						`translate(${i * (xScale.bandwidth() / 3.5 + interBarPadding)}, 
              -${yScale(100 - d)}
            )`
				);

			//Appending X Axis
			const xAxisGroup = group
				.append("g")
				.call(xAxis)
				.attr("transform", `translate(${margin.left}, ${height})`);

			xAxisGroup.select(".domain").attr("opacity", 0);
			xAxisGroup.selectAll(".tick > line").attr("opacity", 0);

			const legend = select(`#bar-${this.subject}-legend`);
			const legendItems = [
				{ name: "Correct", color: "#c8f7e2" },
				{ name: "Incorrect", color: "#fef0c0" },
				{ name: "Unattempted", color: "#c3ecff" },
			];

			legend
				.selectAll("div")
				.data(legendItems)
				.enter()
				.append("div")
				.attr("class", "bar-legend-item")
				.append("div")
				.attr("style", (d) => `background-color: ${d.color}`)
				.attr("class", "bar-legend-item-color");

			legend
				.selectAll(".bar-legend-item")
				.data(legendItems)
				.append("span")
				.attr("class", "bar-legend-item-text")
				.text((d) => d.name);
		},
	},
};
</script>

<style>
.report_card-header {
	display: block;
	margin: 1rem;
	font-size: var(--size-14);
	font-weight: var(--weight-LM);
	color: var(--bg-tertiary);
}

.bar-chart {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	overflow: auto;
}

.bar-legend-item {
	margin: 0 4rem 0 1rem;
}

.bar-legend-item-color {
	width: 20px;
	height: 20px;
	border-radius: 50%;
}

.bar-legend-item-text {
	display: block;
	margin-top: 5px;
	font-family: "Poppins", sans-serif;
}
</style>
